import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import QaRolesCustomSelectField from 'components/QaRolesCustomSelectField';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import { Q_AND_A_ROLES } from 'router/subrouters/DataRoom/models/QAndA';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import EmailField from 'components/EmailField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';

import './AddTeamMemberModal.styles.scss';
import './AddTeamMemberModal.styles.responsive.scss';

type AddTeamMemberModalProps = {
  className?: string;
  buySideGroupId: string;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
};

const AddTeamMemberModal: React.ForwardRefRenderFunction<
  OverlayRef,
  AddTeamMemberModalProps
> = (props, ref) => {
  const { className, buySideGroupId, updateDataRoomMembers } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('add-team-member-modal', className);

  const { t } = useTranslation();
  const commonValidations = useValidations();

  const { buyerQaRole } = useOptions();

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        const { data } = await dataRoomApi.members.inviteBuySideMember(
          buySideGroupId,
          values,
        );

        updateDataRoomMembers?.(data);
        modal.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [buySideGroupId, modal, updateDataRoomMembers, popServerError],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('addTeamMemberLC')}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ qaRole: Q_AND_A_ROLES.DRAFTER }}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('addTeamMemberModalDescription')}</p>

            <div>
              <Field
                name="firstName"
                component={InputField}
                label={t('firstName')}
                validate={commonValidations.lastName}
              />
              <Field
                name="lastName"
                component={InputField}
                label={t('lastName')}
                validate={commonValidations.lastName}
              />
            </div>

            <div>
              <EmailField required />

              <QaRolesCustomSelectField
                name="qaRole"
                label={t('qaRole')}
                portalClassName="add-team-member-qa-roles"
                options={buyerQaRole}
                renderAsPortal
                required={false}
              />
            </div>

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {t('addColleague')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddTeamMemberModal);
